<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="isEdit ? 'Edit Action' : 'New Action'"
    :style="{ width: '90vw', maxWidth: '700px' }"
    :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
    :dismissableMask="true"
  >
    <template #header>
      <div class="flex items-center gap-2 sm:gap-3">
        <div
          class="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0"
          :style="{ backgroundColor: form.colorHex + '20', color: form.colorHex }"
        >
          <i class="pi pi-pencil text-base sm:text-lg"></i>
        </div>
        <div class="min-w-0 flex-1">
          <div class="font-bold text-base sm:text-lg truncate">
            {{ isEdit ? 'Edit Action' : 'New Action' }}
          </div>
          <div
            class="text-[10px] sm:text-xs text-(--p-anthracite-500) truncate"
            v-if="isEdit && props.value"
          >
            ID: {{ props.value.id }} • Created {{ formatCreatedDate(props.value) }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="submit" class="overflow-y-auto">
      <Tabs v-model:value="activeTab">
        <TabList>
          <Tab :value="0"><i class="pi pi-info-circle"></i><span>Details</span></Tab>
          <Tab :value="1">
            <i class="pi pi-paperclip"></i><span>Attachments</span>
            <Badge v-if="(props.value?.attachments || localAttachments || pendingAttachments).length > 0" :value="(props.value?.attachments || localAttachments || pendingAttachments).length" severity="info" />
          </Tab>
          <Tab :value="2">
            <i class="pi pi-comments"></i><span>Comments</span>
            <Badge v-if="allComments.length > 0" :value="allComments.length" severity="info" />
          </Tab>
          <Tab v-if="isEdit && !isReadOnly" :value="3"><i class="pi pi-history"></i><span>History</span></Tab>
        </TabList>
        <TabPanels>
        <TabPanel :value="0">
          <div class="flex flex-col gap-4">
            <div class="field mt-4">
              <label
                for="action-title"
                class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
              >
                <i class="pi pi-tag mr-1"></i>
                Title *
              </label>
              <InputText
                id="action-title"
                v-model="form.title"
                class="w-full"
                :class="{ 'p-invalid': errors.title }"
                placeholder="Enter action title..."
                :maxlength="255"
                :readonly="isReadOnly"
                :disabled="isReadOnly"
                required
                @blur="validateTitle"
                @input="validateTitle"
              />
              <small v-if="errors.title" class="p-error mt-1 block">
                <i class="pi pi-exclamation-circle mr-1"></i>
                {{ errors.title }}
              </small>
              <small
                v-else-if="form.title"
                class="text-xs text-(--p-anthracite-500) mt-1 block"
                :class="{ 'text-red-500': form.title.length > 255 }"
              >
                {{ form.title.length }}/255 characters
              </small>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="field">
                <div class="flex items-center justify-between mb-2">
                  <label
                    for="action-plan"
                    class="block text-sm sm:text-base font-medium text-(--p-anthracite-500)"
                  >
                    <i class="pi pi-folder mr-1"></i>
                    Plan *
                  </label>
                  <Button
                    v-if="form.planId"
                    icon="pi pi-eye"
                    :label="undefined"
                    severity="secondary"
                    size="small"
                    text
                    class="!text-xs !p-1 sm:!p-0 !h-auto min-h-[32px] sm:min-h-0"
                    @click="onSeePlan"
                    v-tooltip.top="'View plan details'"
                  />
                </div>
                <Select
                  id="action-plan"
                  v-model="form.planId"
                  :options="planOptions"
                  optionLabel="title"
                  optionValue="id"
                  placeholder="Select a plan"
                  filter
                  :filter-fields="['title']"
                  filter-placeholder="Search by plan name..."
                  class="w-full"
                  :class="{ 'p-invalid': errors.planId }"
                  :disabled="isReadOnly"
                  required
                  @change="validatePlanId"
                  @blur="validatePlanId"
                />
                <small v-if="errors.planId" class="p-error">{{ errors.planId }}</small>
              </div>

              <div class="field">
                <label
                  for="action-state"
                  class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
                >
                  <i class="pi pi-flag mr-1"></i>
                  {{ hasMultipleRetailerStatus ? 'Overall Status' : 'Status' }} *
                </label>
                <Select
                  id="action-state"
                  v-model="form.state"
                  :options="stateOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select status"
                  class="w-full"
                  :class="{ 'p-invalid': errors.state }"
                  :disabled="isReadOnly"
                  required
                  @change="validateState"
                  @blur="validateState"
                >
                  <template #option="{ option }">
                    <div class="flex items-center gap-2">
                      <i :class="option.icon" :style="{ color: option.color }"></i>
                      <span>{{ option.label }}</span>
                    </div>
                  </template>
                  <template #value="{ value }">
                    <div v-if="value" class="flex items-center gap-2">
                      <i :class="getStateIcon(value)" :style="{ color: getStateColor(value) }"></i>
                      <span>{{ getStateLabel(value) }}</span>
                    </div>
                  </template>
                </Select>
                <small v-if="errors.state" class="p-error">{{ errors.state }}</small>
              </div>
            </div>

            <div v-if="canSetPrivate" class="field">
              <div
                class="flex items-center justify-between p-3 border border-(--p-surface-200) rounded-lg"
              >
                <div>
                  <label class="block text-sm font-medium text-(--p-anthracite-500)">
                    Private Action
                  </label>
                  <small class="text-(--p-anthracite-500)">
                    Users without RSM+ access cannot view this action plan.
                  </small>
                </div>
                <ToggleSwitch v-model="form.isPrivate" :disabled="isReadOnly" />
              </div>
            </div>

            <div
              v-if="hasMultipleRetailerStatus && planRetailers.length > 0 && !isReadOnly"
              class="field"
            >
              <label class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
                <i class="pi pi-building mr-1"></i>
                Status per Retailer
              </label>
              <small class="text-(--p-anthracite-500) mb-3 block">
                Set individual status per Retailer. If not set, the overall status will be used.
              </small>
              <div
                class="space-y-3 border border-(--p-surface-200) rounded-lg p-4 bg-(--p-surface-50)"
              >
                <div
                  v-for="retailer in planRetailers"
                  :key="retailer.id"
                  class="flex items-center justify-between gap-4 p-3 bg-white rounded-lg border border-(--p-surface-200)"
                >
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-(--p-anthracite-500) truncate">
                      {{ retailer.label }}
                    </div>
                    <div v-if="retailer.code" class="text-xs text-(--p-anthracite-500) mt-0.5">
                      Code: {{ retailer.code }}
                    </div>
                  </div>
                  <Select
                    :modelValue="getRetailerStatus(retailer.id)"
                    @update:modelValue="(value) => setRetailerStatus(retailer.id, value)"
                    :options="stateOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Use overall"
                    :disabled="isReadOnly"
                    class="w-40"
                  >
                    <template #option="{ option }">
                      <div class="flex items-center gap-2">
                        <i :class="option.icon" :style="{ color: option.color }"></i>
                        <span>{{ option.label }}</span>
                      </div>
                    </template>
                    <template #value="{ value }">
                      <div v-if="value" class="flex items-center gap-2">
                        <i
                          :class="getStateIcon(value)"
                          :style="{ color: getStateColor(value) }"
                        ></i>
                        <span>{{ getStateLabel(value) }}</span>
                      </div>
                      <span v-else class="text-(--p-anthracite-500)">Use overall</span>
                    </template>
                  </Select>
                </div>
              </div>
            </div>

            <div class="field">
              <label for="action-kpi" class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
                <i class="pi pi-chart-line mr-1"></i>
                KPI Area
              </label>
              <Select
                id="action-kpi"
                v-model="form.kpiArea"
                :options="kpiAreaOptions"
                placeholder="Select KPI area"
                class="w-full"
                :filter="true"
                filterPlaceholder="Search KPI areas..."
                :disabled="isReadOnly"
              />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div class="field sm:col-span-2">
                <label
                  for="action-date"
                  class="block text-sm sm:text-base font-medium text-(--p-anthracite-500) mb-2"
                >
                  <i class="pi pi-calendar mr-1"></i>
                  Due Date *
                </label>
                <DatePicker
                  id="action-date"
                  v-model="form.dueDate"
                  class="w-full"
                  :class="{ 'p-invalid': errors.dueDate }"
                  dateFormat="dd/mm/yy"
                  :showIcon="true"
                  iconDisplay="input"
                  :showButtonBar="true"
                  :minDate="new Date()"
                  :disabled="isReadOnly"
                  required
                  @update:modelValue="validateDueDate"
                  @blur="validateDueDate"
                />
                <small v-if="errors.dueDate" class="p-error">{{ errors.dueDate }}</small>
                <small v-if="daysUntilDue !== null" class="text-(--p-anthracite-500) mt-1 block">
                  {{
                    daysUntilDue > 0
                      ? `${daysUntilDue} days remaining`
                      : daysUntilDue === 0
                        ? 'Due today'
                        : `${Math.abs(daysUntilDue)} days overdue`
                  }}
                </small>
              </div>

              <div class="field">
                <label
                  for="action-color"
                  class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
                >
                  <i class="pi pi-palette mr-1"></i>
                  Color
                </label>
                <div class="flex items-center gap-2">
                  <ColorPicker
                    id="action-color"
                    v-model="form.colorHex"
                    class="flex-1"
                    :disabled="isReadOnly"
                    @update:modelValue="normalizeColorHex"
                  />
                  <div
                    class="w-10 h-10 rounded border-2"
                    :style="{ backgroundColor: form.colorHex, borderColor: form.colorHex }"
                  ></div>
                </div>
              </div>
            </div>

            <div class="field">
              <label
                for="action-details"
                class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
              >
                <i class="pi pi-align-left mr-1"></i>
                Details
              </label>
              <Textarea
                id="action-details"
                v-model="form.details"
                placeholder="Enter additional details for this action..."
                class="w-full"
                rows="4"
                :autoResize="true"
                :readonly="isReadOnly"
                :disabled="isReadOnly"
              />
              <small class="text-(--p-anthracite-500) mt-1 block">
                {{ form.details.length }} characters
              </small>
            </div>
          </div>
        </TabPanel>

        <TabPanel :value="1">
          <div class="flex flex-col gap-4">
            <div class="field">
              <FileUpload
                name="files[]"
                :multiple="true"
                :customUpload="true"
                :auto="true"
                :disabled="uploading"
                chooseLabel="Choose Files"
                uploadLabel="Upload"
                cancelLabel="Clear"
                @uploader="onFilesUpload"
              >
                <template #empty>
                  <div class="flex flex-col items-center justify-center py-8">
                    <i class="pi pi-cloud-upload !text-4xl text-(--p-anthracite-500) mb-3"></i>
                    <p class="text-sm text-(--p-anthracite-500)">Drag and drop files here to upload</p>
                  </div>
                </template>
              </FileUpload>
            </div>

            <div v-if="allAttachments.length > 0" class="field">
              <label class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
                {{ isEdit ? 'Uploaded Files' : 'Pending Files' }}
              </label>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(att, index) in allAttachments"
                  :key="att.id || `pending-${index}`"
                  class="flex items-center justify-between p-3 border border-(--p-surface-200) rounded-lg hover:border-(--p-primary-300) transition-colors"
                >
                  <div class="flex items-center gap-3 flex-1 min-w-0">
                    <i class="pi pi-file !text-xl text-(--p-primary-500)"></i>
                    <div class="flex-1 min-w-0">
                      <div class="font-medium text-sm truncate">
                        {{ att.originalName || (att as { name?: string }).name }}
                      </div>
                      <div class="text-xs text-(--p-anthracite-500)">
                        {{ formatFileSize(att.sizeBytes || (att as { size?: number }).size || 0) }}
                        <span v-if="!isEdit && !att.id" class="text-yellow-600 ml-2"
                          >(Pending upload)</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-1">
                    <Button
                      v-if="isEdit && att.id"
                      icon="pi pi-download"
                      severity="secondary"
                      text
                      rounded
                      size="small"
                      @click="downloadAttachment(att)"
                      v-tooltip.top="'Download file'"
                    />
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      text
                      rounded
                      size="small"
                      @click="removeAttachment(att, index)"
                      v-tooltip.top="'Remove file'"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Message v-else severity="info" :closable="false">
              No attachments yet. Upload files to attach them to this action.
            </Message>
          </div>
        </TabPanel>

        <TabPanel :value="2">
          <div class="flex flex-col gap-4">
            <div class="field">
              <label class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
                Add a comment
              </label>
              <div class="flex gap-2">
                <InputText
                  v-model="newComment"
                  placeholder="Write a comment..."
                  class="flex-1 min-h-[44px]"
                  @keyup.enter="postNewComment"
                />
                <Button
                  icon="pi pi-send"
                  label="Post"
                  @click="postNewComment"
                  :disabled="!newComment.trim()"
                  class="min-h-[44px] sm:min-h-0"
                />
              </div>
            </div>

            <div v-if="allComments.length > 0" class="field">
              <label class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
                Discussion
              </label>
              <div class="flex flex-col gap-3 max-h-96 overflow-y-auto custom-scrollbar pr-2">
                <div
                  v-for="(c, index) in allComments"
                  :key="c.id || `pending-${index}`"
                  class="p-3 rounded-lg border border-(--p-surface-200) bg-(--p-surface-50) hover:border-(--p-primary-300) transition-colors"
                >
                  <div class="flex items-start gap-3 mb-2">
                    <div
                      class="w-8 h-8 rounded-full bg-(--p-primary-500) flex items-center justify-center text-white text-sm font-semibold"
                    >
                      <i class="pi pi-user"></i>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="font-medium text-sm text-(--p-anthracite-500)">
                        {{ getCommentAuthor(c).fullName }}
                        <small
                          v-if="getCommentAuthor(c).username"
                          class="text-(--p-anthracite-500) font-normal"
                        >
                          ({{ getCommentAuthor(c).username }})
                        </small>
                        </span>
                        <span class="text-xs text-(--p-anthracite-500)">
                          {{ c.createdAt ? formatRelativeTime(c.createdAt) : '' }}
                        </span>
                      </div>
                      <div class="text-sm text-(--p-anthracite-500) whitespace-pre-line">
                        {{ c.body }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="c.replies?.length"
                    class="ml-11 mt-3 pl-3 border-l-2 border-(--p-surface-300) space-y-2"
                  >
                    <div v-for="r in c.replies" :key="r.id" class="flex items-start gap-2">
                      <div
                        class="w-6 h-6 rounded-full bg-(--p-surface-300) flex items-center justify-center text-xs"
                      >
                        <i class="pi pi-user text-[10px]"></i>
                      </div>
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <span class="font-medium text-xs text-(--p-anthracite-500)">
                            {{ getCommentAuthor(r).fullName }}
                            <small
                              v-if="getCommentAuthor(r).username"
                              class="text-(--p-anthracite-500) font-normal"
                            >
                              ({{ getCommentAuthor(r).username }})
                            </small>
                          </span>
                          <span class="text-[10px] text-(--p-anthracite-500)">
                            {{ r.createdAt ? formatRelativeTime(r.createdAt) : '' }}
                          </span>
                        </div>
                        <div class="text-xs text-(--p-anthracite-500)">{{ r.body }}</div>
                      </div>
                    </div>
                  </div>

                  <div v-if="c.id" class="mt-3 ml-11 flex gap-2">
                    <InputText
                      v-model="replyBuffer[c.id]"
                      placeholder="Write a reply..."
                      size="small"
                      class="flex-1"
                      @keyup.enter="postReply(c.id)"
                    />
                    <Button
                      icon="pi pi-reply"
                      label="Reply"
                      size="small"
                      text
                      @click="postReply(c.id)"
                      :disabled="!replyBuffer[c.id]?.trim()"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Message v-else severity="info" :closable="false">
              No comments yet. Be the first to comment!
            </Message>
          </div>
        </TabPanel>

        <TabPanel v-if="isEdit && !isReadOnly" :value="3">
          <div class="max-h-[calc(100vh-350px)] overflow-y-auto py-4 sm:max-h-[calc(100vh-300px)]">
            <div v-if="historyLoading" class="flex items-center justify-center py-8">
              <ProgressSpinner />
            </div>
            <div v-else-if="historyError" class="p-4">
              <Message severity="error" :closable="false">
                {{ historyError }}
              </Message>
            </div>
            <div
              v-else-if="actionHistory.length === 0"
              class="flex flex-col items-center justify-center py-12 px-4 text-center"
            >
              <i class="pi pi-inbox text-4xl text-(--p-anthracite-500) mb-3"></i>
              <p class="text-(--p-anthracite-500)">No history available for this action</p>
            </div>
            <div v-else class="relative pl-8">
              <div
                v-for="(log, index) in actionHistory"
                :key="log.id"
                class="relative pb-8 pl-8"
                :class="{
                  'timeline-item-last': index === actionHistory.length - 1,
                }"
              >
                <div
                  v-if="index !== actionHistory.length - 1"
                  class="absolute left-2 top-10 -bottom-2 w-0.5 bg-(--p-surface-200)"
                ></div>
                <div
                  class="absolute -left-6 top-1 w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm z-10"
                  :class="{
                    'bg-(--p-mint-500) text-white': log.event === 'created',
                    'bg-blue-500 text-white': log.event === 'updated',
                    'bg-red-500 text-white': log.event === 'deleted',
                    'bg-amber-500 text-white': log.event === 'restored',
                  }"
                >
                  <i
                    :class="[
                      getEventIcon(log.event),
                      ['created', 'updated', 'deleted', 'restored'].includes(log.event)
                        ? 'text-white'
                        : '',
                    ]"
                  ></i>
                </div>
                <div
                  class="bg-(--p-surface-50) border border-(--p-surface-200) rounded-lg p-4 transition-all hover:shadow-md hover:border-(--p-primary-300)"
                >
                  <div
                    class="flex justify-between items-center mb-3 flex-wrap gap-2 sm:flex-col sm:items-start"
                  >
                    <div class="flex items-center gap-2">
                      <Badge
                        :value="getEventLabel(log.event, log.auditable_type)"
                        :severity="getEventSeverity(log.event)"
                        class="capitalize"
                      />
                      <span class="text-xs text-(--p-anthracite-500) font-medium">{{
                        formatHistoryDate(log.created_at)
                      }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-xs text-(--p-anthracite-500)">
                      <i class="pi pi-user"></i>
                      <span>{{ getUserDisplayName(log.user) }}</span>
                    </div>
                  </div>
                  <div class="timeline-body">
                    <div
                      v-if="getEventDescription(log, 'action')"
                      class="text-sm text-(--p-anthracite-500) mb-3"
                    >
                      {{ getEventDescription(log, 'action') }}
                    </div>
                    <div
                      v-if="log.changed_attributes && log.changed_attributes.length > 0"
                      class="mt-2 space-y-2"
                    >
                      <div
                        v-for="attr in log.changed_attributes"
                        :key="attr"
                        class="change-item p-3 bg-(--p-surface-50) rounded-lg border border-(--p-surface-200)"
                      >
                        <div class="flex items-center gap-2 mb-2">
                          <i class="pi pi-arrow-right text-xs text-(--p-primary-500)"></i>
                          <span class="text-sm font-semibold text-(--p-anthracite-500)">
                            {{ getFieldLabel(attr) }}
                          </span>
                        </div>
                        <div
                          v-if="log.old_values && log.old_values[attr] !== undefined"
                          class="flex items-start gap-3"
                        >
                          <div class="flex-1">
                            <div class="text-xs text-(--p-anthracite-500) mb-1">Previous</div>
                            <div class="text-sm text-(--p-anthracite-500) line-through text-red-600">
                              {{ formatFieldValue(attr, log.old_values[attr]) }}
                            </div>
                          </div>
                          <i class="pi pi-arrow-right text-xs text-(--p-primary-500) mt-4"></i>
                          <div class="flex-1">
                            <div class="text-xs text-(--p-anthracite-500) mb-1">Current</div>
                            <div class="text-sm font-medium text-(--p-anthracite-500)">
                              {{ formatFieldValue(attr, log.new_values?.[attr]) }}
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-sm text-(--p-anthracite-500)">
                          {{ formatFieldValue(attr, log.new_values?.[attr]) }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-else-if="log.new_values && Object.keys(log.new_values).length > 0"
                      class="mt-2 p-3 bg-(--p-mint-300) rounded-lg border border-(--p-mint-500)"
                    >
                      <div class="text-sm font-semibold text-(--p-mint-700) mb-2">
                        <i class="pi pi-check-circle mr-1"></i>
                        {{ getEventLabel(log.event, log.auditable_type) }}
                      </div>
                      <div class="space-y-1">
                        <div
                          v-for="(value, key) in log.new_values"
                          :key="key"
                          class="text-sm text-(--p-anthracite-500)"
                        >
                          <span class="font-medium">{{ getFieldLabel(key) }}:</span>
                          <span class="ml-2">{{ formatFieldValue(key, value) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
        </TabPanels>
      </Tabs>
    </form>

    <template #footer>
      <div class="flex flex-row items-stretch justify-between w-full gap-3 sm:gap-0">
        <div v-if="!isReadOnly && isEdit" class="flex gap-2 justify-center">
          <Button
            type="button"
            :label="isMobile ? undefined : 'Duplicate'"
            icon="pi pi-copy"
            severity="secondary"
            text
            :disabled="deleting"
            @click="duplicate"
            class="min-h-[44px] min-w-[44px] rounded-lg sm:min-h-0 sm:min-w-0"
          />
          <Button
            type="button"
            :label="isMobile ? undefined : 'Delete'"
            icon="pi pi-trash"
            severity="danger"
            outlined
            :loading="deleting"
            @click="confirmDelete"
            class="min-h-[44px] min-w-[44px] rounded-lg sm:min-h-0 sm:min-w-0"
          />
        </div>

        <div class="flex flex-1 justify-end gap-2 w-full sm:w-auto">
          <Button
            v-if="!isReadOnly && !isMobile"
            type="button"
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            outlined
            :disabled="deleting"
            @click="cancel"
            class="flex-1 sm:flex-none min-h-[48px] sm:min-h-0 rounded-lg font-medium"
          />
          <Button
            v-if="!isReadOnly"
            type="submit"
            :label="!isMobile ? (isEdit ? 'Save Changes' : 'Create Action') : undefined"
            icon="pi pi-check"
            severity="primary"
            :loading="saving"
            :disabled="deleting || uploading"
            :class="{ 'opacity-50 cursor-not-allowed': !isFormValid && !saving }"
            class="min-h-[48px] sm:min-h-0 rounded-lg font-semibold"
            @click="
              (e) => {
                if (!isFormValid && !saving && !uploading && !deleting) {
                  e.preventDefault()
                  showValidationErrors()
                } else {
                  submit()
                }
              }
            "
          />
          <Button
            v-if="isReadOnly"
            type="button"
            label="Close"
            icon="pi pi-times"
            severity="secondary"
            outlined
            @click="cancel"
            class="flex-1 sm:flex-none min-h-[48px] sm:min-h-0 rounded-lg font-medium"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showDeleteConfirm"
    modal
    header="Delete Action"
    :style="{ width: '90vw', maxWidth: '500px' }"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <i class="pi pi-exclamation-triangle text-red-600 text-2xl"></i>
        <span class="font-bold text-lg">Delete Action</span>
      </div>
    </template>
    <div class="p-4">
      <p class="mb-4 text-(--p-anthracite-500)">
        Are you sure you want to delete the action
        <strong>"{{ props.value?.title }}"</strong>?
      </p>
      <p class="text-sm text-(--p-anthracite-500)">
        This action will soft delete the action. It will be hidden from view but can be restored if
        needed.
      </p>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          severity="secondary"
          outlined
          :disabled="deleting"
          @click="showDeleteConfirm = false"
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          :loading="deleting"
          @click="handleDelete"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref, onMounted, onUnmounted } from 'vue'
import type { Action, ActionAttachment, ActionComment } from '@/config/types/Action'
import type { Plan } from '@/config/types/Plan'
import type { KpiArea } from '@/config/types/Action'
import { KPI_AREAS } from '@/config/constants/KpiAreas'
import useActions, {
  uploadActionAttachment,
  deleteActionAttachment,
  downloadActionAttachment,
  fetchActionAttachments,
  fetchActionComments,
  postActionComment,
} from '@/composables/Actions'
import useAuditLogs, { type AuditLog } from '@/composables/AuditLogs'
import {
  getEventIcon,
  getEventSeverity,
  getUserDisplayName,
  formatHistoryDate,
  getFieldLabel,
  formatFieldValue,
  getEventLabel,
  getEventDescription,
} from '@/composables/useAuditLogFormatter'
import { formatInputDate, parseInputDate, formatRelativeTime } from '@/utils/formatters'
import useNotifications from '@/composables/Notification'
import useAuth from '@/composables/Auth'
import type { UploaderEvent } from '@/config/types/ActionEditor'

const props = defineProps<{
  modelValue: boolean
  value?: Action | null
  defaultDate?: string
  defaultState?: 'pending' | 'active' | 'complete'
  plans?: Plan[]
  currentPlanId?: string | number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'save', value: Action & { planId: string | number }): void
  (e: 'cancel'): void
  (e: 'duplicated', value: Action): void
  (e: 'editDuplicated', value: Action): void
  (e: 'deleted', actionId: string | number): void
  (e: 'view:plan', planId: string | number): void
}>()

const { getStateIcon, getStateColor, getAllStateOptions, getStateLabel } = useActions()
const stateOptions = getAllStateOptions()

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const isMobile = computed(() => windowWidth.value < 640)

const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value)
    if (!value) {
      activeTab.value = 0
      actionHistory.value = []
      historyError.value = null
      form.id = undefined
      form.title = ''
      form.dueDate = new Date()
      form.state = 'pending'
       form.colorHex = '#243782'
      form.planId = null
      form.kpiArea = null
      form.details = ''
      form.isPrivate = true
      form.retailerStatuses = []
      localAttachments.value = []
      pendingAttachments.value = []
      pendingComments.value = []
      comments.value = []
      newComment.value = ''
      replyBuffer.value = {}
      Object.keys(errors).forEach((key) => delete errors[key as keyof typeof errors])
      emit('cancel')
    }
  },
})
const isEdit = computed(() => Boolean(props.value && props.value.id))

const { user } = useAuth()
const isReadOnly = computed(() => {
  const userAccess = (user.value as { access?: number })?.access
  return userAccess !== undefined && userAccess !== null && userAccess < 5
})
const canSetPrivate = computed(() => {
  const userAccess = (user.value as { access?: number })?.access
  return userAccess !== undefined && userAccess !== null && userAccess >= 5
})
const getCommentAuthor = (comment: ActionComment) => {
  const source = comment.user || (user.value as { forename?: string; surname?: string; username?: string })
  const fullName = [source?.forename, source?.surname].filter(Boolean).join(' ').trim()
  const username = source?.username || ''

  return {
    fullName: fullName || username || 'User',
    username,
  }
}

const form = reactive<{
  id?: string | number
  title: string
  dueDate: Date | null
  state: 'pending' | 'active' | 'complete'
  colorHex: string
  planId: string | number | null
  kpiArea: KpiArea | null
  details: string
  isPrivate: boolean
  retailerStatuses: Array<{
    retailerId: number | string
    state: 'pending' | 'active' | 'complete'
  }>
}>({
  id: undefined,
  title: '',
  dueDate: new Date(),
  state: 'pending',
  colorHex: '#243782',
  planId: null,
  kpiArea: null,
  details: '',
  isPrivate: true,
  retailerStatuses: [],
})
const saving = ref(false)
const uploading = ref(false)
const deleting = ref(false)
const showDeleteConfirm = ref(false)
const localAttachments = ref<ActionAttachment[]>([])
const pendingAttachments = ref<File[]>([])
const comments = ref<ActionComment[]>([])
const pendingComments = ref<Array<{ body: string; parentId?: string | number | null }>>([])
const newComment = ref('')
const replyBuffer = ref<Record<string | number, string>>({})
const activeTab = ref(0)
const actionHistory = ref<AuditLog[]>([])
const historyLoading = ref(false)
const historyError = ref<string | null>(null)

const errors = reactive<{
  title?: string
  planId?: string
  state?: string
  dueDate?: string
}>({})

const isFormValid = computed(() => {
  if (!form.title.trim()) {
    return false
  }
  if (form.title.length > 255) {
    return false
  }
  if (!form.planId) {
    return false
  }
  if (!form.state) {
    return false
  }
  if (!form.dueDate) {
    return false
  }
  return true
})

const { duplicateAction, deleteAction, createAction } = useActions()
const { toast } = useNotifications()
const { fetchAuditLogs, logAuditEvent } = useAuditLogs()
const lastViewedActionId = ref<number | string | null>(null)

type PendingAttachment = {
  id?: undefined
  originalName: string
  sizeBytes: number
  name: string
  size: number
}

const allAttachments = computed(() => {
  const uploaded = props.value?.attachments || localAttachments.value || []
  const pending = pendingAttachments.value.map(
    (file): PendingAttachment => ({
      id: undefined,
      originalName: file.name,
      sizeBytes: file.size,
      name: file.name,
      size: file.size,
    }),
  )
  return [...uploaded, ...pending] as Array<ActionAttachment | PendingAttachment>
})

const allComments = computed(() => {
  const loaded = comments.value || []
  const pending = pendingComments.value.map((comment) => ({
    id: undefined,
    body: comment.body,
    createdAt: new Date().toISOString(),
    user: {
      forename: (user.value as { forename?: string })?.forename,
      surname: (user.value as { surname?: string })?.surname,
      username: (user.value as { username?: string })?.username || 'User',
    },
    replies: [],
  }))
  return [...loaded, ...pending]
})

async function onFilesUpload(e: UploaderEvent) {
  uploading.value = true
  const files: File[] = Array.isArray(e.files) ? e.files : [e.files]
  const maxFileSize = 50 * 1024 * 1024 // 50MB (matches API validation)

  // Validate file sizes before processing
  const oversizedFiles = files.filter((file) => file.size > maxFileSize)
  if (oversizedFiles.length > 0) {
    const fileNames = oversizedFiles.map((f) => f.name).join(', ')
    toast(
      'File Too Large',
      `The following file(s) exceed the 50MB limit: ${fileNames}. Please choose smaller files.`,
      'error',
    )
    uploading.value = false
    return
  }

  try {
    if (form.id) {
      // If action exists, upload immediately
      const uploadResults: Array<{ success: boolean; file: File; error?: string }> = []

      for (const file of files) {
        try {
          const uploaded = await uploadActionAttachment(form.id, file)
          localAttachments.value = [...(props.value?.attachments || []), uploaded]
          uploadResults.push({ success: true, file })
        } catch (error: unknown) {
          const errorMessage = getUploadErrorMessage(error, file.name)
          uploadResults.push({ success: false, file, error: errorMessage })
          toast('Upload Failed', `Failed to upload "${file.name}": ${errorMessage}`, 'error')
        }
      }

      // Show summary if some files failed
      const failedCount = uploadResults.filter((r) => !r.success).length
      if (failedCount > 0 && failedCount < files.length) {
        toast(
          'Partial Upload',
          `${files.length - failedCount} file(s) uploaded successfully, ${failedCount} failed.`,
          'warn',
        )
      }
    } else {
      pendingAttachments.value.push(...files)
      toast(
        'Files Added',
        `${files.length} file(s) will be uploaded when you save the action.`,
        'info',
      )
    }
  } catch (error: unknown) {
    const errorMessage = getUploadErrorMessage(error, 'files')
    toast('Upload Error', `Failed to process files: ${errorMessage}`, 'error')
  } finally {
    uploading.value = false
  }
}

function getUploadErrorMessage(error: unknown, fileName: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number
        data?: { errors?: Record<string, string[]>; message?: string }
      }
    }
    const status = axiosError.response?.status
    const data = axiosError.response?.data

    if (status === 413 || status === 422) {
      if (data?.errors?.file) {
        return `${fileName}: ${data.errors.file.join(', ')}`
      }
      if (data?.message) {
        return `${fileName}: ${data.message}`
      }
      return `${fileName}: File size exceeds the maximum allowed limit (50MB)`
    }
    if (status === 415) {
      return `${fileName}: File type not allowed`
    }
    if (status === 500) {
      return `${fileName}: Server error occurred during upload`
    }
    if (data?.message) {
      return `${fileName}: ${data.message}`
    }
  } else if (error && typeof error === 'object' && 'request' in error) {
    return `${fileName}: Network error - please check your connection`
  } else if (error instanceof Error) {
    return `${fileName}: ${error.message}`
  }
  return `${fileName}: Unknown error occurred`
}

async function downloadAttachment(att: ActionAttachment | PendingAttachment) {
  if (!form.id || !att.id) return

  try {
    const fileName = att.originalName || (att as { name?: string }).name || 'attachment'
    await downloadActionAttachment(form.id, att.id, fileName)
    toast('Download Started', `"${fileName}" is being downloaded.`, 'success')
  } catch (error: unknown) {
    const errorMessage =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to download attachment'
        : error instanceof Error
          ? error.message
          : 'Failed to download attachment'
    toast('Error', `Failed to download attachment: ${errorMessage}`, 'error')
  }
}

async function removeAttachment(att: ActionAttachment | PendingAttachment, index?: number) {
  if (form.id && att.id) {
    // Remove uploaded attachment
    try {
      await deleteActionAttachment(form.id, att.id)
      localAttachments.value = (props.value?.attachments || []).filter((a) => a.id !== att.id)
      toast('Attachment Removed', 'The attachment has been removed successfully.', 'success')
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            'Failed to remove attachment'
          : error instanceof Error
            ? error.message
            : 'Failed to remove attachment'
      toast('Error', `Failed to remove attachment: ${errorMessage}`, 'error')
    }
  } else if (index !== undefined) {
    // Remove pending attachment by index
    const removedFile = pendingAttachments.value[index]
    pendingAttachments.value.splice(index, 1)
    toast('File Removed', `"${removedFile.name}" has been removed from the upload queue.`, 'info')
  } else {
    // Try to find and remove by name
    const fileName =
      (att as { name?: string; originalName?: string }).name ||
      (att as { name?: string; originalName?: string }).originalName
    if (fileName) {
      const fileIndex = pendingAttachments.value.findIndex((f) => f.name === fileName)
      if (fileIndex >= 0) {
        pendingAttachments.value.splice(fileIndex, 1)
        toast('File Removed', `"${fileName}" has been removed from the upload queue.`, 'info')
      }
    }
  }
}

watch(
  () => form.id,
  async (id) => {
    if (!id) return
    try {
      comments.value = await fetchActionComments(id)
      localAttachments.value = await fetchActionAttachments(id)
    } catch {}
  },
  { immediate: true },
)

async function postNewComment() {
  if (!newComment.value.trim()) return

  if (form.id) {
    const created = await postActionComment(form.id, newComment.value.trim())
    comments.value.push(created)
  } else {
    pendingComments.value.push({ body: newComment.value.trim() })
  }
  newComment.value = ''
}

async function postReply(parentId: string | number) {
  const body = (replyBuffer.value[parentId] || '').trim()
  if (!body) return

  if (form.id) {
    const created = await postActionComment(form.id, body, parentId)
    const parent = comments.value.find((c) => c.id === parentId)
    if (parent) {
      parent.replies = parent.replies || []
      parent.replies.push(created)
    }
  } else {
    const parent = allComments.value.find((c) => c.id === parentId)
    if (parent) {
      if (!parent.replies) {
        parent.replies = []
      }
      const pendingReply = {
        id: undefined as unknown as number | string,
        body: body,
        createdAt: new Date().toISOString(),
      } as ActionComment
      ;(parent.replies as ActionComment[]).push(pendingReply)
      pendingComments.value.push({ body: body, parentId: parentId })
    }
  }
  replyBuffer.value[parentId] = ''
}

const planOptions = computed(() => {
  return props.plans || []
})

const currentPlan = computed(() => {
  if (!form.planId) return null
  return planOptions.value.find((p) => p.id === form.planId) || null
})

const planRetailers = computed(() => {
  const plan = currentPlan.value
  if (!plan) return []

  const retailers: Array<{ id: number | string; label: string; code?: string }> = []

  if (plan.retailers && plan.retailers.length > 0) {
    retailers.push(...plan.retailers)
  }

  return retailers
})

const hasMultipleRetailerStatus = computed(() => {
  const plan = currentPlan.value
  if (!plan) return false

  if (plan.retailers && plan.retailers.length > 1) {
    return true
  }

  if (plan.groups && plan.groups.length > 0 && plan.retailers && plan.retailers.length > 0) {
    return true
  }

  return false
})

const getRetailerStatus = (
  retailerId: number | string,
): 'pending' | 'active' | 'complete' | null => {
  const normalizedId = Number(retailerId)
  const status = form.retailerStatuses.find((rs) => Number(rs.retailerId) === normalizedId)
  return status ? status.state : null
}

const setRetailerStatus = (
  retailerId: number | string,
  state: 'pending' | 'active' | 'complete' | null,
) => {
  const normalizedId = Number(retailerId)
  const index = form.retailerStatuses.findIndex((rs) => Number(rs.retailerId) === normalizedId)

  if (state === null) {
    if (index >= 0) {
      form.retailerStatuses.splice(index, 1)
    }
  } else {
    if (index >= 0) {
      form.retailerStatuses[index].state = state
    } else {
      form.retailerStatuses.push({ retailerId: Number(retailerId), state })
    }
  }
}

watch(
  () => form.planId,
  (newPlanId, oldPlanId) => {
    if (newPlanId !== oldPlanId && oldPlanId != null && newPlanId != null) {
      const plan = currentPlan.value
      if (plan && planRetailers.value.length > 0) {
        if (form.id) {
          const retailerIds = new Set(planRetailers.value.map((r) => Number(r.id)))
          form.retailerStatuses = form.retailerStatuses.filter((rs) =>
            retailerIds.has(Number(rs.retailerId)),
          )
        } else {
          form.retailerStatuses = []
        }
      }
    }
  },
)

const daysUntilDue = computed(() => {
  if (!form.dueDate) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(form.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  const diffTime = dueDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
})

const formatCreatedDate = (action: Action) => {
  const createdAt = (action as Action & { createdAt?: string }).createdAt
  if (!createdAt) return ''
  return formatRelativeTime(createdAt)
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const kpiAreaOptions: KpiArea[] = [...KPI_AREAS]

watch(activeTab, (newIndex) => {
  if (newIndex === 3 && isEdit.value && props.value?.id && !isReadOnly.value) {
    loadHistory()
  }
})

watch(
  () => [props.value, props.defaultDate, props.defaultState, props.modelValue, props.currentPlanId],
  () => {
    activeTab.value = 0
    actionHistory.value = []
    historyError.value = null
    Object.keys(errors).forEach((key) => delete errors[key as keyof typeof errors])

    if (!props.modelValue) {
      return
    }

    if (props.value) {
      form.id = props.value.id
      form.title = props.value.title
      form.dueDate = parseInputDate(props.value.dueDate)
      const colorValue = props.value.colorHex || '#60a5fa'
      form.colorHex = colorValue.startsWith('#') ? colorValue : `#${colorValue}`
      form.state = props.value.state
      form.planId = props.currentPlanId || null
      form.kpiArea = props.value.kpiArea || null
      form.details = props.value.details || ''
      form.isPrivate = props.value.isPrivate ?? false
      if (props.value.retailerStatuses && Array.isArray(props.value.retailerStatuses)) {
        form.retailerStatuses = props.value.retailerStatuses.map((rs) => ({
          retailerId: Number(rs.retailerId),
          state: rs.state,
        }))
      } else {
        form.retailerStatuses = []
      }

      localAttachments.value = props.value.attachments || []
      pendingAttachments.value = []
    } else {
      const hadPendingItems =
        pendingAttachments.value.length > 0 || pendingComments.value.length > 0

      form.id = undefined
      form.title = ''
      form.dueDate =
        props.defaultDate && props.defaultDate.trim()
          ? parseInputDate(props.defaultDate)
          : new Date()
      form.state = props.defaultState || 'pending'
       form.colorHex = '#243782'
      form.planId = props.currentPlanId || planOptions.value[0]?.id || null
      form.kpiArea = null
      form.details = ''
      form.isPrivate = true
      form.retailerStatuses = []
      if (!hadPendingItems) {
        pendingAttachments.value = []
        pendingComments.value = []
      }

      localAttachments.value = []
    }
  },
  { immediate: true },
)

watch(
  () => [props.modelValue, props.value?.id],
  async ([isOpen, actionId]) => {
    if (!isOpen || !actionId) {
      lastViewedActionId.value = null
      return
    }

    const normalizedId = Number(actionId)
    if (Number.isNaN(normalizedId)) {
      return
    }

    if (lastViewedActionId.value === normalizedId) {
      return
    }

    lastViewedActionId.value = normalizedId

    try {
      await logAuditEvent({
        auditableType: 'Action',
        auditableId: normalizedId,
        event: 'viewed',
      })
    } catch (error) {
      console.warn('Failed to log action view:', error)
    }
  },
)

const validateTitle = () => {
  if (!form.title.trim()) {
    errors.title = 'Title is required'
  } else if (form.title.length > 255) {
    errors.title = 'Title must be 255 characters or less'
  } else {
    delete errors.title
  }
}

const validatePlanId = () => {
  if (!form.planId) {
    errors.planId = 'Plan is required'
  } else {
    delete errors.planId
  }
}

const onSeePlan = () => {
  if (form.planId) {
    emit('view:plan', form.planId)
  }
}

const validateState = () => {
  if (!form.state) {
    errors.state = 'Status is required'
  } else {
    delete errors.state
  }
}

const validateDueDate = () => {
  if (!form.dueDate) {
    errors.dueDate = 'Due date is required'
  } else {
    delete errors.dueDate
  }
}

const normalizeColorHex = (value: string | null | undefined) => {
  if (!value) {
     form.colorHex = '#243782'
    return
  }

  form.colorHex = value.startsWith('#') ? value : `#${value}`
}

const validateForm = () => {
  validateTitle()
  validatePlanId()
  validateState()
  validateDueDate()
  return Object.keys(errors).length === 0
}

function showValidationErrors() {
  const errorMessages: string[] = []

  if (errors.title) {
    errorMessages.push(errors.title)
  }
  if (errors.planId) {
    errorMessages.push(errors.planId)
  }
  if (errors.state) {
    errorMessages.push(errors.state)
  }
  if (errors.dueDate) {
    errorMessages.push(errors.dueDate)
  }

  if (errorMessages.length > 0) {
    const message =
      errorMessages.length === 1
        ? errorMessages[0]
        : `Please fix the following errors:\n• ${errorMessages.join('\n• ')}`
    toast('Validation Error', message, 'error')
  } else {
    if (!form.title.trim()) {
      toast('Validation Error', 'Title is required', 'error')
    } else if (form.title.length > 255) {
      toast('Validation Error', 'Title must be 255 characters or less', 'error')
    } else if (!form.planId) {
      toast('Validation Error', 'Plan is required', 'error')
    } else if (!form.state) {
      toast('Validation Error', 'Status is required', 'error')
    } else if (!form.dueDate) {
      toast('Validation Error', 'Due date is required', 'error')
    }
  }
}

const cancel = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

async function submit() {
  if (!validateForm()) {
    toast('Validation Error', 'Please fix the errors in the form', 'error')
    return
  }
  if (!form.dueDate || !form.planId) return

  const payload: Action & { planId: string | number } = {
    id: form.id || 0,
    title: form.title.trim(),
    dueDate: formatInputDate(form.dueDate),
    colorHex: (() => {
      const color = form.colorHex
      return color ? (color.startsWith('#') ? color : `#${color}`) : undefined
    })(),
    state: form.state,
    kpiArea: form.kpiArea || undefined,
    details: form.details.trim() || undefined,
    isPrivate: form.isPrivate,
    planId: form.planId,
    retailerStatuses: hasMultipleRetailerStatus.value ? form.retailerStatuses : undefined,
  }

  saving.value = true

  if (!form.id) {
    try {
      const createdAction = await createAction(payload)

      const attachmentErrors: Array<{ file: File; error: string }> = []
      if (pendingAttachments.value.length > 0) {
        uploading.value = true
        try {
          for (const file of pendingAttachments.value) {
            try {
              await uploadActionAttachment(createdAction.id, file)
            } catch (error: unknown) {
              const errorMessage = getUploadErrorMessage(error, file.name)
              attachmentErrors.push({ file, error: errorMessage })
              console.error(`Failed to upload attachment "${file.name}":`, error)
            }
          }

          const failedFileNames = new Set(attachmentErrors.map((e) => e.file.name))
          pendingAttachments.value = pendingAttachments.value.filter(
            (f) => !failedFileNames.has(f.name),
          )

          if (attachmentErrors.length > 0) {
            const failedNames = attachmentErrors.map((e) => e.file.name).join(', ')
            toast(
              'Some Attachments Failed',
              `Action created successfully, but ${attachmentErrors.length} attachment(s) failed to upload: ${failedNames}. You can try uploading them again after saving.`,
              'warn',
            )
          }
        } finally {
          uploading.value = false
        }
      }

      const commentErrors: Array<{ body: string; error: string }> = []
      if (pendingComments.value.length > 0) {
        for (const comment of pendingComments.value) {
          try {
            await postActionComment(createdAction.id, comment.body, comment.parentId)
          } catch (error: unknown) {
            const errorMessage =
              error && typeof error === 'object' && 'response' in error
                ? (error as { response?: { data?: { message?: string } } }).response?.data
                    ?.message || 'Failed to post comment'
                : error instanceof Error
                  ? error.message
                  : 'Failed to post comment'
            commentErrors.push({ body: comment.body, error: errorMessage })
            console.error('Failed to post comment:', error)
          }
        }

        const failedCommentBodies = new Set(commentErrors.map((e) => e.body))
        pendingComments.value = pendingComments.value.filter(
          (c) => !failedCommentBodies.has(c.body),
        )

        if (commentErrors.length > 0) {
          toast(
            'Some Comments Failed',
            `Action created successfully, but ${commentErrors.length} comment(s) failed to post. You can try posting them again after saving.`,
            'warn',
          )
        }
      }

      const createdPayload: Action & { planId: string | number } = {
        ...createdAction,
        planId: form.planId,
      }
      emit('save', createdPayload)

      if (attachmentErrors.length === 0 && commentErrors.length === 0) {
        pendingAttachments.value = []
        pendingComments.value = []
      }

      emit('update:modelValue', false)
    } catch (error: unknown) {
      saving.value = false

      let errorMessage = 'Failed to create action. Please try again.'
      let errorTitle = 'Error Creating Action'

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: {
            status?: number
            data?: { errors?: Record<string, string[]>; message?: string }
          }
        }
        const status = axiosError.response?.status
        const data = axiosError.response?.data

        if (status === 422) {
          errorTitle = 'Validation Error'
          if (data?.errors && typeof data.errors === 'object') {
            const errorMessages = Object.values(data.errors).flat() as string[]
            errorMessage = errorMessages.join(', ') || 'Please check your input and try again.'
          } else if (data?.message) {
            errorMessage = data.message
          }
        } else if (status === 401) {
          errorTitle = 'Authentication Error'
          errorMessage = 'Your session has expired. Please refresh the page and try again.'
        } else if (status === 403) {
          errorTitle = 'Permission Denied'
          errorMessage = 'You do not have permission to create actions in this plan.'
        } else if (status === 404) {
          errorTitle = 'Plan Not Found'
          errorMessage = 'The selected plan could not be found. Please select a different plan.'
        } else if (status && status >= 500) {
          errorTitle = 'Server Error'
          errorMessage = 'An error occurred on the server. Please try again later.'
        } else if (data?.message) {
          errorMessage = data.message
        }
      }

      toast(errorTitle, errorMessage, 'error')
      return
    }
  } else {
    const commentErrors: Array<{ body: string; error: string }> = []
    if (pendingComments.value.length > 0) {
      for (const comment of pendingComments.value) {
        try {
          await postActionComment(form.id, comment.body, comment.parentId)
          if (form.id) {
            comments.value = await fetchActionComments(form.id)
          }
        } catch (error: unknown) {
          const errorMessage =
            error && typeof error === 'object' && 'response' in error
              ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
                'Failed to post comment'
              : error instanceof Error
                ? error.message
                : 'Failed to post comment'
          commentErrors.push({ body: comment.body, error: errorMessage })
          console.error('Failed to post comment:', error)
        }
      }

      const failedCommentBodies = new Set(commentErrors.map((e) => e.body))
      pendingComments.value = pendingComments.value.filter((c) => !failedCommentBodies.has(c.body))

      if (commentErrors.length > 0) {
        toast(
          'Some Comments Failed',
          `${commentErrors.length} comment(s) failed to post. The action will still be saved.`,
          'warn',
        )
      }
    }

    try {
      emit('save', payload)

      if (commentErrors.length === 0) {
        pendingComments.value = []
      }
    } catch {
      saving.value = false
      toast('Error', 'Failed to save action. Please try again.', 'error')
      return
    }
  }

  saving.value = false
}

async function duplicate() {
  if (!props.value?.id) return

  try {
    const duplicatedAction = await duplicateAction(props.value.id)
    emit('editDuplicated', duplicatedAction)
    toast('Action Duplicated', `Action "${duplicatedAction.title}" has been created successfully.`)
  } catch (error) {
    console.error('Failed to duplicate action:', error)
  }
}

function confirmDelete() {
  showDeleteConfirm.value = true
}

async function handleDelete() {
  if (!props.value?.id) return

  deleting.value = true
  try {
    await deleteAction(props.value.id)
    emit('deleted', props.value.id)
    emit('update:modelValue', false)
    toast(
      'Action Deleted',
      `Action "${props.value.title}" has been deleted successfully.`,
      'success',
    )
  } catch {
    toast('Error', 'Failed to delete action. Please try again.', 'error')
  } finally {
    deleting.value = false
    showDeleteConfirm.value = false
  }
}

async function loadHistory() {
  if (!isEdit.value || !props.value?.id || isReadOnly.value) {
    actionHistory.value = []
    return
  }

  historyLoading.value = true
  historyError.value = null

  try {
    const response = await fetchAuditLogs({
      model_type: 'Action',
      model_id: Number(props.value.id),
      per_page: 100,
      page: 1,
    })
    actionHistory.value = response.data || []
  } catch {
    historyError.value = 'Failed to load action history. Please try again.'
    actionHistory.value = []
  } finally {
    historyLoading.value = false
  }
}
</script>

<style scoped>
:deep(.p-tabpanels) {
  padding: 0;
  min-height: 300px;
}

@media (min-width: 640px) {
  :deep(.p-tabpanels) {
    min-height: 400px;
  }
}

:deep(.p-tablist) {
  margin-bottom: 1rem;
}

:deep(.p-tab) {
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
}

@media (min-width: 640px) {
  :deep(.p-tab) {
    padding: 0.75rem 1rem;
    font-size: 0.9375rem;
  }
}

@media (max-width: 639px) {
  :deep(.p-select) {
    min-height: 44px;
  }

  :deep(.p-select-label) {
    padding: 0.75rem;
    font-size: 0.875rem;
  }

  :deep(.p-select-panel) {
    width: calc(100vw - 2rem) !important;
    max-width: 400px !important;
  }

  :deep(.p-select-items-wrapper) {
    max-height: 50vh;
  }
}

@media (max-width: 639px) {
  :deep(.p-calendar) {
    min-height: 44px;
  }

  :deep(.p-calendar-input) {
    padding: 0.75rem;
    font-size: 0.875rem;
  }

  :deep(.p-datepicker) {
    width: calc(100vw - 2rem) !important;
    max-width: 350px !important;
  }
}

@media (max-width: 639px) {
  :deep(.p-colorpicker) {
    min-height: 44px;
  }

  :deep(.p-colorpicker-preview) {
    width: 2.5rem;
    height: 2.5rem;
  }
}

@media (max-width: 639px) {
  :deep(.p-fileupload-buttonbar) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  :deep(.p-fileupload-buttonbar .p-button) {
    min-height: 44px;
    flex: 1 1 auto;
    min-width: calc(50% - 0.25rem);
  }
}

/* Diff and pre formatting */
.diff-old pre,
.diff-new pre,
.timeline-new-values pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.7rem;
  line-height: 1.4;
}

.a11y-reduced-motion :deep(.p-dialog),
.a11y-reduced-motion :deep(.p-tab) {
  transition: none !important;
}

.a11y-reduced-motion .bg-\(--p-surface-50\) {
  transition: none !important;
}

@media (max-width: 639px) {
  :deep(.p-dialog-footer) {
    padding: 1rem;
    border-top: 1px solid var(--p-surface-200);
    background: var(--p-surface-0);
  }

  :deep(.p-dialog-footer .p-button) {
    font-size: 0.9375rem;
    letter-spacing: 0.01em;
  }

  :deep(.p-dialog-footer .p-button.p-button-primary) {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  :deep(.p-dialog-footer .p-button:active) {
    transform: scale(0.98);
  }
}
</style>
